import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import BrandHeader from "../../components/BrandHeader";
import BraceletVisual from "../../components/BraceletVisual";
import ElementBadge from "../../components/ElementBadge";
import { ordersApi } from "../../lib/api";
import { BRAND, PRODUCT, ORDER_STATUSES, MAKING_CHECKLIST, WRIST_SIZES } from "../../lib/constants";
import { buildBraceletLayout, INTENTIONS } from "../../lib/crystalPrescription";
import { crystalColor } from "../../lib/crystalColors";
import { ELEMENT_GLYPH, ELEMENTS, whyDominant } from "../../lib/panchaEngine";
import { renderReport } from "../../lib/sacredReports";
import { CHAKRA } from "../../lib/chakra";
import { calcPersonalYear, getCurrentYear, getNextYear } from "../../lib/numerology";
import {
  getCrystalGradient, ORB_SHADOW, ELEMENT_PALETTE,
  LP_ARCHETYPE, NN_ARCHETYPE, ZODIAC_ARCHETYPE, PY_THEME,
  zodiacCelestialPhrase, CHAKRA_MANTRAS, CHAKRA_YANTRAS,
  SECTION_SUBTITLES, RX_BADGE_COLORS, badgeKeyFromSource,
  BRAND_LOTUS_SVG, FOOTER_EMBLEM_SVG, AFFIRMATION_LOTUS_SVG,
} from "../../lib/sacredReportAssets";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ordersApi.get(id).then(setOrder).catch(() => {});
  }, [id]);

  if (!order) return (
    <div className="min-h-screen">
      <BrandHeader adminMode />
      <div className="p-10 text-[#6B5E7E]">Loading...</div>
    </div>
  );

  const { numerology, panchaBeads, prescriptionCrystals, dominantElement: rawDominantElement, fullName } = order;
  // Normalise to guard against legacy orders where dominantElement was stored
  // as "Fire" / "Earth" (capitalised) or missing, and against any element key
  // that isn't in the current ELEMENTS map ("space" instead of "ether" etc).
  const domElKey = (rawDominantElement || "").toString().toLowerCase();
  const dominantElement = ELEMENTS[domElKey] ? domElKey : (ELEMENTS[rawDominantElement] ? rawDominantElement : "");
  const elDef = ELEMENTS[dominantElement] || { name: rawDominantElement || "\u2014", sanskrit: "\u2014", symbol: "" };
  const elGlyph = ELEMENT_GLYPH[dominantElement] || "";
  const luckyNums = Array.isArray(numerology?.luckyNumbers) ? numerology.luckyNumbers : [];
  const layout = buildBraceletLayout(panchaBeads, prescriptionCrystals);
  const curYr = getCurrentYear();
  const nxtYr = getNextYear();
  // Recompute Personal Years from DOB so labels are always current (handles legacy stored value).
  const pyCur = order.dob ? calcPersonalYear(order.dob, curYr) : numerology.personalYear;
  const pyNxt = order.dob ? calcPersonalYear(order.dob, nxtYr) : numerology.personalYearNext;
  const report = renderReport(numerology.lifePath, fullName, {
    currentYear: curYr,
    nextYear: nxtYr,
    nameNumber: numerology.nameNumber,
  });
  const chakra = CHAKRA[dominantElement] || { name: "\u2014", english: "Chakra", color: "\u2014", location: "\u2014", mantra: "\u2014", governs: "\u2014" };
  const wristDef = WRIST_SIZES.find((w) => w.value === order.wristSize);
  const checklist = order.checklist || {};
  const selectedIntentions = (order.intentions || [])
    .map((k) => INTENTIONS.find((i) => i.key === k)?.label)
    .filter(Boolean);

  const update = async (patch) => {
    setSaving(true);
    try {
      const updated = await ordersApi.update(order.id, patch);
      setOrder(updated);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const toggleChecklist = (key) => {
    const next = { ...checklist, [key]: !checklist[key] };
    update({ checklist: next });
  };

  const copyWhatsapp = () => {
    navigator.clipboard.writeText(buildWhatsappMessage(order, { curYr, nxtYr, pyCur, pyNxt, chakra }));
    toast.success("WhatsApp message copied");
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(buildEmailContent(order, report, { curYr, nxtYr, pyCur, pyNxt, chakra }));
    toast.success("Email content copied");
  };

  return (
    <div className="min-h-screen" data-testid="order-detail-page">
      <BrandHeader adminMode />

      <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 print-area">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 flex-wrap no-print">
          <div>
            <Link to="/admin/orders" className="text-sm text-[#6A0DAD] hover:underline" data-testid="back-to-orders">&larr; All Orders</Link>
            <h1 className="font-serif text-4xl text-[#2C0A5E] mt-3">{fullName}</h1>
            <div className="text-[#6B5E7E] text-sm mt-1">{order.orderNumber} &middot; {new Date(order.createdAt).toLocaleString("en-IN")}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => window.print()} className="ph-btn-ghost" data-testid="print-prescription">Print Prescription Card</button>
            <button onClick={() => window.print()} className="ph-btn-ghost" data-testid="print-report">Print Sacred Report</button>
            <button onClick={copyEmail} className="ph-btn-ghost" data-testid="copy-email">Copy Email Content</button>
            <button onClick={copyWhatsapp} className="ph-btn-primary" data-testid="copy-whatsapp">Copy WhatsApp Message</button>
          </div>
        </div>

        {/* Print hint — only visible in the admin view, hidden in PDF */}
        <div
          className="no-print"
          data-testid="print-hint"
          style={{
            background: "#FFF8E1",
            border: "1px solid #D4A855",
            borderRadius: "8px",
            padding: "12px 16px",
            margin: "16px 0 0",
            fontSize: "13px",
            color: "#8B6914",
            fontFamily: "Jost, sans-serif",
            lineHeight: 1.5,
          }}
        >
          <strong>Saving as PDF:</strong> when the print dialog opens, expand <em>More settings</em> and uncheck <em>&ldquo;Headers and footers&rdquo;</em> for a clean PDF. Keep <em>Background graphics</em> enabled so gemstone colours, chakra gradients and the affirmation card render correctly.
        </div>

        {/* Customer profile */}
        <div className="grid lg:grid-cols-3 gap-5 mt-8">
          <div className="ph-card p-6 lg:col-span-2">
            <div className="ph-overline">Customer Profile</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Info label="Full name" value={fullName} />
              <Info label="Date of birth" value={order.dob} />
              {order.gender && order.gender !== "-" && (
                <Info label="Gender" value={order.gender} />
              )}
              <Info label="Email" value={order.email ? <a className="text-[#6A0DAD] hover:underline" href={`mailto:${order.email}`}>{order.email}</a> : "-"} />
              <Info label="WhatsApp" value={<a className="text-[#6A0DAD] hover:underline" href={`https://wa.me/${(order.whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer">{order.whatsapp}</a>} />
              <Info label="Wrist size" value={wristDef ? `${wristDef.label} (${wristDef.description})` : order.wristSize} />
              <Info label="Order" value={`${PRODUCT.name} - Rs ${PRODUCT.price}`} />
              {selectedIntentions.length > 0 && (
                <Info label="Intentions" value={selectedIntentions.join(", ")} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <span className="ph-badge">Life Path <strong className="ml-1 text-[#6A0DAD]">{numerology.lifePath}</strong></span>
              <span className="ph-badge">Name Number <strong className="ml-1 text-[#6A0DAD]">{numerology.nameNumber}</strong></span>
              <span className="ph-badge">Zodiac <strong className="ml-1 text-[#6A0DAD]">{numerology.zodiac}</strong></span>
              <span className="ph-badge">Personal Year <strong className="ml-1 text-[#6A0DAD]">{pyCur}</strong></span>
              <ElementBadge element={dominantElement}>{elGlyph} Dominant {elDef.name}</ElementBadge>
            </div>
            {order.notes && (
              <div className="mt-5">
                <div className="ph-overline">Customer Notes</div>
                <p className="text-sm text-[#2C0A5E] mt-1">{order.notes}</p>
              </div>
            )}

            {/* Delivery address */}
            <div className="mt-6">
              <div className="ph-overline">Delivery Address</div>
              <DeliveryAddressEditor order={order} onSaved={(o) => setOrder(o)} />
            </div>
          </div>

          {/* Status panel */}
          <div className="ph-card p-6 no-print">
            <div className="ph-overline">Status</div>
            <select
              className="ph-input mt-3"
              value={order.status}
              disabled={saving}
              onChange={(e) => update({ status: e.target.value })}
              data-testid="status-select"
            >
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="ph-overline mt-5">Payment</div>
            <select
              className="ph-input mt-3"
              value={order.paymentStatus}
              disabled={saving}
              onChange={(e) => update({ paymentStatus: e.target.value })}
              data-testid="payment-select"
            >
              <option value="Pending Payment">Pending Payment</option>
              <option value="Payment Confirmed">Payment Confirmed</option>
              <option value="Refunded">Refunded</option>
            </select>
            <div className="ph-overline mt-5">Payment Method</div>
            <select
              className="ph-input mt-3"
              value={order.paymentMethod || ""}
              disabled={saving}
              onChange={(e) => update({ paymentMethod: e.target.value })}
              data-testid="method-select"
            >
              <option value="">Not set</option>
              <option value="UPI">UPI</option>
              <option value="Razorpay">Razorpay</option>
              <option value="Cash">Cash</option>
              <option value="Bank Transfer">Bank Transfer</option>
            </select>
            <div className="ph-overline mt-5">Internal Notes</div>
            <textarea
              rows={4}
              className="ph-input mt-3"
              defaultValue={order.internalNotes || ""}
              onBlur={(e) => update({ internalNotes: e.target.value })}
              data-testid="internal-notes"
              placeholder="Private to you"
            />

            {order.inventory_decremented_at && (
              <div
                className="mt-5 rounded-lg p-3"
                style={{
                  background: "rgba(212, 168, 85, 0.10)",
                  border: "1px solid rgba(212, 168, 85, 0.45)",
                }}
                data-testid="inventory-decrement-notice"
              >
                <div className="ph-overline" style={{ color: "#8A6D2F" }}>Inventory Decremented</div>
                <div className="text-xs mt-1" style={{ color: "#4B3A6E" }}>
                  {new Date(order.inventory_decremented_at).toLocaleString()}
                </div>
                {Array.isArray(order.inventory_decrement_details?.items) && (
                  <ul className="text-xs mt-2 space-y-0.5" style={{ color: "#4B3A6E" }}>
                    {order.inventory_decrement_details.items.map((it) => (
                      <li key={it.crystalName} className="flex justify-between gap-2">
                        <span>{it.crystalName}</span>
                        <span style={{ color: it.stockAfter < 0 ? "#C13B1B" : "#4B3A6E" }}>
                          {it.delta} → {it.stockAfter} in stock
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ❋ ✦ ❋  Pancha Element Foundation  ❋ ✦ ❋ */}
        <section className="mt-12">
          <SacredDivider />
          <SacredSectionTitle title="PANCHA FOUNDATION" subtitle={SECTION_SUBTITLES["PANCHA FOUNDATION"]} />
          <p className="text-[#6B5E7E] text-sm text-center max-w-2xl mx-auto -mt-2 mb-6 italic" style={{ fontFamily: "Georgia, serif" }}>
            {whyDominant(dominantElement, numerology.lifePath, numerology.nameNumber, numerology.zodiac)}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mt-5 print-card pancha-grid">
            {panchaBeads.map((b) => {
              const pal = ELEMENT_PALETTE[b.element] || ELEMENT_PALETTE.earth;
              return (
                <div
                  key={b.element}
                  className="rounded-xl text-center"
                  style={{
                    background: `linear-gradient(180deg, ${pal.bg} 0%, white 100%)`,
                    border: `1px solid ${pal.border}`,
                    padding: "14px 6px",
                  }}
                  data-testid={`pancha-orb-${b.element}`}
                >
                  <div
                    className="mx-auto mb-2"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: getCrystalGradient(b.crystal),
                      boxShadow: ORB_SHADOW,
                    }}
                    aria-hidden
                  />
                  <div
                    style={{
                      fontSize: "9px",
                      letterSpacing: "0.18em",
                      color: pal.label,
                      fontFamily: "Jost, sans-serif",
                      textTransform: "uppercase",
                    }}
                  >
                    {b.element}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      fontStyle: "italic",
                      color: pal.sanskrit,
                      fontFamily: "Georgia, serif",
                      marginBottom: "3px",
                    }}
                  >
                    {ELEMENTS[b.element]?.sanskrit || b.element}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#1A1030",
                      fontWeight: 500,
                      fontFamily: "'Cormorant Garamond', serif",
                      lineHeight: 1.2,
                    }}
                  >
                    {b.crystal}
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      color: "#6B5E7E",
                      marginTop: "4px",
                      letterSpacing: "0.1em",
                      fontFamily: "Jost, sans-serif",
                    }}
                  >
                    8mm
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ❋ ✦ ❋  Prescription Crystals  ❋ ✦ ❋ */}
        <section className="mt-12">
          <SacredDivider />
          <SacredSectionTitle title="PRESCRIPTION CRYSTALS" subtitle={SECTION_SUBTITLES["PRESCRIPTION CRYSTALS"]} />
          <div className="grid md:grid-cols-2 gap-3 mt-4 print-card">
            {prescriptionCrystals.map((c, i) => {
              const badgeKey = badgeKeyFromSource(c.source);
              const badge = RX_BADGE_COLORS[badgeKey] || RX_BADGE_COLORS["LIFE PATH"];
              const isLastOrphan = i === prescriptionCrystals.length - 1 && prescriptionCrystals.length % 2 === 1;
              return (
                <div
                  key={`${c.crystal}-${i}`}
                  className="rounded-xl flex gap-3 items-start"
                  style={{
                    background: "white",
                    border: "1px solid rgba(212,168,85,0.4)",
                    padding: "14px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    gridColumn: isLastOrphan ? "1 / -1" : undefined,
                    maxWidth: isLastOrphan ? "50%" : undefined,
                    marginLeft: isLastOrphan ? "auto" : undefined,
                    marginRight: isLastOrphan ? "auto" : undefined,
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      background: getCrystalGradient(c.crystal),
                      boxShadow: ORB_SHADOW,
                    }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div style={{ fontSize: "15px", color: "#2C0A5E", fontWeight: 500, fontFamily: "'Cormorant Garamond', serif" }}>
                      {c.crystal}
                    </div>
                    <span
                      className="inline-block"
                      style={{
                        background: badge.bg,
                        color: badge.color,
                        padding: "2px 8px",
                        borderRadius: "8px",
                        fontSize: "9px",
                        letterSpacing: "0.15em",
                        marginTop: "3px",
                        fontFamily: "Jost, sans-serif",
                      }}
                    >
                      {badgeKey}
                    </span>
                    <span
                      className="inline-block ml-2"
                      style={{
                        fontSize: "10px",
                        color: "#6B5E7E",
                        letterSpacing: "0.08em",
                        fontFamily: "Jost, sans-serif",
                      }}
                    >
                      {c.beadCount}× 8mm
                    </span>
                    <p style={{ fontSize: "11px", color: "#6B5E7E", marginTop: "5px", fontStyle: "italic", fontFamily: "Georgia, serif", lineHeight: 1.5 }}>
                      {c.reason}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ❋ ✦ ❋  Bead Arrangement  ❋ ✦ ❋ */}
        <section className="mt-12 print-card">
          <SacredDivider />
          <SacredSectionTitle title="BEAD ARRANGEMENT" subtitle="Your bracelet, visualized" />
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="md:w-1/2 flex items-center justify-center">
              <BraceletVisual layout={layout} compact />
            </div>
            <div
              className="md:w-1/2"
              style={{
                background: "white",
                border: "1px solid rgba(212,168,85,0.2)",
                borderRadius: "12px",
                padding: "18px 22px",
              }}
            >
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }} className="space-y-2">
                {panchaBeads.map((b, i) => (
                  <li key={b.element} className="flex items-center gap-2 text-sm" style={{ color: "#1A1030", fontFamily: "Georgia, serif" }}>
                    <span
                      className="w-3 h-3 rounded-full border inline-block shrink-0"
                      style={{ borderColor: "#D4A855", backgroundColor: crystalColor(b.crystal) }}
                    />
                    Group {i + 1}: {ELEMENTS[b.element]?.sanskrit || b.element} 8mm ({b.crystal}) + prescription beads
                  </li>
                ))}
              </ul>
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid rgba(212,168,85,0.2)",
                  fontSize: "13px",
                  color: "#1A1030",
                  fontFamily: "Georgia, serif",
                }}
              >
                Total beads: <strong style={{ color: "#6A0DAD" }}>{layout.length}</strong>
              </div>
            </div>
          </div>
        </section>

        {/* ❋ ✦ ❋ ✦ ❋  Sacred Report  ❋ ✦ ❋ ✦ ❋ */}
        {report && (
          <section
            className="mt-14 p-6 md:p-10 print-card rounded-2xl"
            data-testid="sacred-report"
            style={{ background: "linear-gradient(135deg, #FAF8F4 0%, #F5F0E8 100%)" }}
          >
            {/* Brand header */}
            <div className="text-center sacred-report-header" style={{ paddingBottom: "14px" }}>
              {/* SAFETY: BRAND_LOTUS_SVG is a hardcoded SVG constant in sacredReportAssets.js — no user input. Sanitized via DOMPurify as defence-in-depth. */}
              <span
                style={{ display: "inline-block", marginBottom: "10px" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(BRAND_LOTUS_SVG, { USE_PROFILES: { svg: true, svgFilters: true } }) }}
              />
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "28px",
                  color: "#2C0A5E",
                  letterSpacing: "0.04em",
                  fontWeight: 500,
                  marginBottom: "4px",
                }}
              >
                Pancha Healing
              </div>
              <div
                style={{
                  fontSize: "9px",
                  letterSpacing: "0.32em",
                  color: "#6A0DAD",
                  fontFamily: "Jost, sans-serif",
                  fontWeight: 500,
                }}
              >
                SACRED PRESCRIPTION
              </div>
            </div>
            <div
              style={{
                height: "1px",
                background: "linear-gradient(to right, transparent, #D4A855, transparent)",
                margin: "0 auto 24px",
              }}
            />

            {/* Customer name editorial */}
            <div
              className="text-center"
              style={{
                padding: "6px 0 22px",
                borderBottom: "1px solid rgba(212,168,85,0.3)",
                marginBottom: "26px",
              }}
            >
              <p
                style={{
                  margin: "0 0 4px",
                  fontSize: "11px",
                  letterSpacing: "0.3em",
                  color: "#6B5E7E",
                  fontFamily: "Jost, sans-serif",
                }}
              >
                A SACRED BLUEPRINT FOR
              </p>
              <h1
                style={{
                  margin: "0 0 6px",
                  fontSize: "38px",
                  fontWeight: 400,
                  color: "#2C0A5E",
                  fontStyle: "italic",
                  fontFamily: "'Cormorant Garamond', serif",
                  lineHeight: 1.05,
                }}
              >
                {fullName}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#6B5E7E",
                  fontFamily: "Jost, sans-serif",
                }}
              >
                {formatDobLong(order.dob)} · Born under the {zodiacCelestialPhrase(numerology.zodiac)}
              </p>
            </div>

            {/* Sacred Blueprint Grid — 3 rows with highlighted PY cells */}
            <SacredDivider />
            <SacredSectionTitle title="SACRED BLUEPRINT" subtitle={SECTION_SUBTITLES["SACRED BLUEPRINT"]} />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-1" data-testid="sacred-blueprint-grid">
              <BPCell label="LIFE PATH" number={numerology.lifePath} subtitle={LP_ARCHETYPE[numerology.lifePath]} />
              <BPCell label="NAME NUMBER" number={numerology.nameNumber} subtitle={NN_ARCHETYPE[numerology.nameNumber]} />
              <BPCell label="ZODIAC" numberText={numerology.zodiac} subtitle={ZODIAC_ARCHETYPE[numerology.zodiac]} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_1.5fr] gap-2.5 mt-2.5">
              <BPHighlightCell
                kind="now"
                label={`PERSONAL YEAR ${curYr}`}
                number={pyCur}
                subtitle={PY_THEME[pyCur]}
              />
              <BPHighlightCell
                kind="next"
                label={`PERSONAL YEAR ${nxtYr}`}
                number={pyNxt}
                subtitle={PY_THEME[pyNxt]}
              />
              <BPCell label="RULING PLANET" numberText={numerology.rulingPlanet} subtitle="Cosmic guide" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-2.5">
              <BPCell label="LUCKY COLOUR" text={numerology.luckyColor} />
              <BPCell label="LUCKY DAY" text={numerology.luckyDay} />
              <BPCell label="LUCKY NUMBERS" text={luckyNums.length ? luckyNums.join(", ") : "\u2014"} />
            </div>

            {/* Dominant Chakra card with yantra + Devanagari bija */}
            {chakra && (
              <div className="chakra-section">
                <SacredDivider />
                <SacredSectionTitle title="DOMINANT CHAKRA" subtitle={SECTION_SUBTITLES["DOMINANT CHAKRA"]} />
                <div
                  className="rounded-2xl text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #FFE5E5 0%, #FFEDD5 16%, #FEF9C3 33%, #DCFCE7 50%, #DBEAFE 66%, #E0E7FF 83%, #F3E8FF 100%)",
                    padding: "28px 24px",
                    border: "1px solid rgba(212,168,85,0.5)",
                    boxShadow: "0 4px 24px rgba(106,13,173,0.08)",
                  }}
                  data-testid="chakra-card"
                >
                  {/* SAFETY: CHAKRA_YANTRAS is a hardcoded SVG dictionary in sacredReportAssets.js — no user input. Sanitized via DOMPurify as defence-in-depth. */}
                  <div
                    style={{ display: "inline-block", marginBottom: "10px" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(CHAKRA_YANTRAS[chakra.name] || "", { USE_PROFILES: { svg: true, svgFilters: true } }) }}
                    data-testid={`chakra-yantra-${chakra.name}`}
                  />
                  <div
                    style={{
                      fontSize: "30px",
                      color: "#2C0A5E",
                      fontWeight: 500,
                      letterSpacing: "0.04em",
                      marginBottom: "4px",
                      fontFamily: "'Cormorant Garamond', serif",
                    }}
                  >
                    {chakra.name}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#8B47D9",
                      letterSpacing: "0.18em",
                      fontStyle: "italic",
                      fontFamily: "Jost, sans-serif",
                      marginBottom: "18px",
                    }}
                  >
                    {chakra.english.toUpperCase()} · {elDef.name.toUpperCase()} · {(CHAKRA_MANTRAS[chakra.name]?.petals || "")} PETALS
                  </div>

                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left"
                    style={{
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "12px",
                      padding: "16px 20px",
                      marginBottom: "18px",
                    }}
                  >
                    <ChakraCellV2 label="ELEMENT" value={`${elDef.name} (${elDef.sanskrit})`} />
                    <ChakraCellV2 label="SACRED COLOR" value={chakra.color} />
                    <ChakraCellV2 label="LOCATION" value={chakra.location} />
                    <ChakraCellV2 label="GOVERNS" value={chakra.governs} />
                  </div>

                  {/* Bija Mantra — Roman + Devanagari */}
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        fontSize: "10px",
                        letterSpacing: "0.25em",
                        color: "#6A0DAD",
                        fontFamily: "Jost, sans-serif",
                        marginBottom: "6px",
                      }}
                    >
                      YOUR BIJA MANTRA
                    </div>
                    <div className="flex items-center justify-center gap-4">
                      <div
                        style={{
                          fontSize: "54px",
                          color: "#2C0A5E",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          lineHeight: 1,
                          fontFamily: "'Cormorant Garamond', serif",
                        }}
                        data-testid="bija-roman"
                      >
                        {CHAKRA_MANTRAS[chakra.name]?.roman || chakra.mantra}
                      </div>
                      <div style={{ width: "1px", height: "44px", background: "rgba(106,13,173,0.3)" }} />
                      <div
                        style={{
                          fontSize: "54px",
                          color: "#2C0A5E",
                          lineHeight: 1,
                          fontFamily: "'Tiro Devanagari Sanskrit', serif",
                        }}
                        data-testid="bija-devanagari"
                      >
                        {CHAKRA_MANTRAS[chakra.name]?.devanagari || ""}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#8B47D9",
                        fontStyle: "italic",
                        fontFamily: "Georgia, serif",
                        marginTop: "10px",
                      }}
                    >
                      Chant 108 times daily to awaken your {chakra.name}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Soul Essence with drop cap */}
            <SacredDivider />
            <SacredSectionTitle title="SOUL ESSENCE" subtitle={SECTION_SUBTITLES["SOUL ESSENCE"]} big />
            <SoulEssenceCard firstName={fullName.split(" ")[0]} body={report.soul} />

            {/* Long-form sections */}
            <SacredDivider />
            <SacredSectionTitle title="LIFE PATH MISSION" subtitle={SECTION_SUBTITLES["LIFE PATH MISSION"]} />
            <ContentCard body={report.lp} />

            <SacredDivider />
            <SacredSectionTitle title="NAME NUMBER ENERGY" subtitle={SECTION_SUBTITLES["NAME NUMBER ENERGY"]} />
            <ContentCard body={report.nn} />

            <SacredDivider />
            <SacredSectionTitle title="CORE CHARACTERISTICS" subtitle={SECTION_SUBTITLES["CORE CHARACTERISTICS"]} />
            <ContentCard body={report.ch} />

            <SacredDivider />
            <SacredSectionTitle title={`PERSONAL YEAR · ${curYr}`} subtitle={SECTION_SUBTITLES["PERSONAL YEAR"]} />
            <ContentCard body={report.cur} />

            <SacredDivider />
            <SacredSectionTitle title={`ENERGY AHEAD · ${nxtYr}`} subtitle={SECTION_SUBTITLES["ENERGY AHEAD"]} />
            <ContentCard body={report.fut} />

            <SacredDivider />
            <SacredSectionTitle title="KARMA LESSON" subtitle={SECTION_SUBTITLES["KARMA LESSON"]} />
            <ContentCard body={report.kar} />

            <SacredDivider />
            <SacredSectionTitle title="SACRED GIFTS" subtitle={SECTION_SUBTITLES["SACRED GIFTS"]} />
            <ContentCard body={report.gifts} />

            <SacredDivider />
            <SacredSectionTitle title="SPIRITUAL PRACTICES" subtitle={SECTION_SUBTITLES["SPIRITUAL PRACTICES"]} />
            <ContentCard body={report.prac} />

            {/* Sacred Guidance */}
            <SacredDivider />
            <SacredSectionTitle title="SACRED GUIDANCE" subtitle={SECTION_SUBTITLES["SACRED GUIDANCE"]} />
            <div className="grid sm:grid-cols-2 gap-3.5">
              <GuidanceCard variant="do" items={report.dos} />
              <GuidanceCard variant="avoid" items={report.dont} />
            </div>

            {/* Personal Affirmation — emotional climax */}
            <SacredDivider big />
            <SacredSectionTitle title="YOUR SACRED AFFIRMATION" subtitle={SECTION_SUBTITLES["SACRED AFFIRMATION"]} big />
            <div
              className="rounded-2xl text-center relative affirmation-card"
              data-testid="affirmation-card"
              style={{
                background: "linear-gradient(135deg, #2C0A5E 0%, #6A0DAD 100%)",
                padding: "36px 28px 32px",
                boxShadow: "0 12px 36px rgba(106,13,173,0.25)",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: "-1px",
                  borderRadius: "16px",
                  border: "1px solid rgba(212,168,85,0.6)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  fontSize: "14px",
                  color: "#D4A855",
                  letterSpacing: "0.4em",
                  marginBottom: "16px",
                  fontFamily: "Jost, sans-serif",
                }}
              >
                ✦ ✦ ✦
              </div>
              {/* SAFETY: AFFIRMATION_LOTUS_SVG is a hardcoded SVG constant in sacredReportAssets.js — no user input. Sanitized via DOMPurify as defence-in-depth. */}
              <span
                style={{ display: "inline-block", marginBottom: "10px" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(AFFIRMATION_LOTUS_SVG, { USE_PROFILES: { svg: true, svgFilters: true } }) }}
              />
              <p
                style={{
                  margin: "0 0 14px",
                  fontSize: "20px",
                  lineHeight: 1.7,
                  color: "white",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontStyle: "italic",
                }}
              >
                &ldquo;{report.aff}&rdquo;
              </p>
              <div
                style={{
                  fontSize: "14px",
                  color: "#D4A855",
                  letterSpacing: "0.4em",
                  marginTop: "14px",
                  fontFamily: "Jost, sans-serif",
                }}
              >
                ✦ ✦ ✦
              </div>
            </div>

            {/* Brand footer */}
            <div
              className="text-center"
              style={{
                marginTop: "32px",
                paddingTop: "20px",
                borderTop: "1px solid rgba(212,168,85,0.3)",
              }}
            >
              {/* SAFETY: FOOTER_EMBLEM_SVG is a hardcoded SVG constant in sacredReportAssets.js — no user input. Sanitized via DOMPurify as defence-in-depth. */}
              <span
                style={{ display: "inline-block" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(FOOTER_EMBLEM_SVG, { USE_PROFILES: { svg: true, svgFilters: true } }) }}
              />
              <div
                style={{
                  fontSize: "18px",
                  color: "#2C0A5E",
                  fontStyle: "italic",
                  margin: "8px 0 4px",
                  fontFamily: "'Cormorant Garamond', serif",
                }}
              >
                Pancha Healing
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6B5E7E",
                  letterSpacing: "0.25em",
                  fontFamily: "Jost, sans-serif",
                }}
              >
                YOUR CRYSTALS · PRESCRIBED
              </div>
            </div>
          </section>
        )}

        {/* Making checklist */}
        <section className="mt-10 ph-card p-6 no-print">
          <div className="ph-overline">Making Checklist</div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {MAKING_CHECKLIST.map((item) => (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer text-sm text-[#2C0A5E]" data-testid={`checklist-${item.key}`}>
                <input type="checkbox" className="mt-1 w-4 h-4 accent-[#6A0DAD]" checked={!!checklist[item.key]} onChange={() => toggleChecklist(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="ph-overline">{label}</div>
      <div className="text-[#2C0A5E] mt-1">{value || "-"}</div>
    </div>
  );
}

function ChakraCell({ label, value }) {
  return (
    <div className="rounded-lg px-3 py-2" style={{ backgroundColor: "rgba(0,0,0,0.25)" }}>
      <div className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "#FFD97A" }}>{label}</div>
      <div className="text-sm mt-0.5" style={{ color: "#FAF7F2" }}>{value}</div>
    </div>
  );
}

// ─────────── v5.1 visual helpers ───────────

function formatDobLong(dob) {
  if (!dob) return "";
  const [y, m, d] = dob.split("-").map((x) => parseInt(x, 10));
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  if (!y || !m || !d) return dob;
  return `${d} ${months[m - 1]} ${y}`;
}

function SacredDivider({ big = false }) {
  return (
    <div className="text-center" style={{ margin: big ? "36px 0 6px" : "30px 0 6px" }}>
      <span style={{ color: "#D4A855", fontSize: big ? "22px" : "18px", letterSpacing: big ? "0.3em" : "0.2em" }}>
        {big ? "❋ ✦ ❋ ✦ ❋" : "❋ ✦ ❋"}
      </span>
    </div>
  );
}

function SacredSectionTitle({ title, subtitle, big = false }) {
  return (
    <>
      <h2
        className="text-center section-title"
        style={{
          margin: big ? "10px 0 8px" : "8px 0 6px",
          fontSize: big ? "15px" : "13px",
          letterSpacing: big ? "0.32em" : "0.28em",
          color: "#6A0DAD",
          fontFamily: "Jost, sans-serif",
          fontWeight: 500,
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className="text-center section-subtitle"
          style={{
            margin: "0 0 18px",
            fontSize: big ? "13px" : "12px",
            color: "#6B5E7E",
            fontFamily: "Georgia, serif",
            fontStyle: "italic",
          }}
        >
          {subtitle}
        </p>
      )}
    </>
  );
}

function BPCell({ label, number, numberText, text, subtitle }) {
  return (
    <div
      className="text-center"
      style={{
        background: "white",
        border: "1px solid rgba(106,13,173,0.15)",
        borderRadius: "12px",
        padding: "14px 12px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          letterSpacing: "0.2em",
          color: "#6B5E7E",
          fontFamily: "Jost, sans-serif",
          marginBottom: "6px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      {number != null && (
        <div style={{ fontSize: "36px", color: "#6A0DAD", fontWeight: 400, lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }}>
          {number}
        </div>
      )}
      {numberText && (
        <div style={{ fontSize: "22px", color: "#6A0DAD", fontWeight: 400, paddingTop: "4px", fontFamily: "'Cormorant Garamond', serif" }}>
          {numberText}
        </div>
      )}
      {text && (
        <div style={{ fontSize: "13px", color: "#2C0A5E", fontStyle: "italic", fontFamily: "Georgia, serif", paddingTop: "4px" }}>
          {text}
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: "10px", color: "#6B5E7E", marginTop: "4px", fontStyle: "italic", fontFamily: "Georgia, serif" }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function BPHighlightCell({ kind, label, number, subtitle }) {
  const isNow = kind === "now";
  const bg = isNow
    ? "linear-gradient(135deg, #FFF8E1 0%, #FFE9B0 100%)"
    : "linear-gradient(135deg, #F5F0FF 0%, #E8DBFF 100%)";
  const border = isNow ? "#D4A855" : "rgba(106,13,173,0.4)";
  const text = isNow ? "#8B6914" : "#6A0DAD";
  const badge = isNow ? "#D4A855" : "#6A0DAD";
  return (
    <div
      className="text-center relative"
      data-testid={`py-${kind}`}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        padding: "14px",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "6px",
          right: "8px",
          background: badge,
          color: "white",
          fontSize: "8px",
          padding: "2px 6px",
          borderRadius: "8px",
          letterSpacing: "0.1em",
          fontFamily: "Jost, sans-serif",
        }}
      >
        {isNow ? "NOW" : "NEXT"}
      </div>
      <div style={{ fontSize: "9px", letterSpacing: "0.2em", color: text, fontFamily: "Jost, sans-serif", marginBottom: "6px", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: "36px", color: text, fontWeight: 400, lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }}>
        {number}
      </div>
      {subtitle && (
        <div style={{ fontSize: "10px", color: text, marginTop: "4px", fontStyle: "italic", fontFamily: "Georgia, serif" }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function ChakraCellV2({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: "9px", letterSpacing: "0.18em", color: "#6A0DAD", fontFamily: "Jost, sans-serif", marginBottom: "2px" }}>
        {label}
      </div>
      <div style={{ fontSize: "13px", color: "#1A1030", fontFamily: "Jost, sans-serif" }}>{value}</div>
    </div>
  );
}

function SoulEssenceCard({ firstName, body }) {
  const firstLetter = (firstName || "").charAt(0) || "";
  const rest = body || "";
  return (
    <div
      style={{
        background: "linear-gradient(180deg, white 0%, #FAF8F4 100%)",
        border: "1px solid rgba(212,168,85,0.3)",
        borderRadius: "16px",
        padding: "28px 26px",
      }}
    >
      <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, 'Cormorant Garamond', serif" }}>
        <span
          style={{
            float: "left",
            fontSize: "56px",
            lineHeight: 0.85,
            padding: "6px 10px 0 0",
            color: "#6A0DAD",
            fontWeight: 500,
            fontFamily: "'Cormorant Garamond', serif",
          }}
          aria-hidden
        >
          {firstLetter}
        </span>
        {rest}
      </p>
    </div>
  );
}

function ContentCard({ body }) {
  return (
    <div
      style={{
        padding: "0 8px",
        marginBottom: "24px",
        maxWidth: "640px",
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, serif" }}>
        {body}
      </p>
    </div>
  );
}

function GuidanceCard({ variant, items }) {
  const isDo = variant === "do";
  const bg = isDo ? "linear-gradient(180deg, #F0FDF4 0%, white 100%)" : "linear-gradient(180deg, #FEF2F2 0%, white 100%)";
  const border = isDo ? "#22C55E" : "#EF4444";
  const labelColor = isDo ? "#15803D" : "#991B1B";
  const label = isDo ? "SACRED GUIDANCE — DO" : "SACRED GUIDANCE — AVOID";
  const iconPath = isDo ? (
    <>
      <circle cx="6" cy="6" r="5" fill="none" stroke="#22C55E" strokeWidth="1" />
      <path d="M3.5,6 L5.5,8 L8.5,4" fill="none" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ) : (
    <>
      <circle cx="6" cy="6" r="5" fill="none" stroke="#EF4444" strokeWidth="1" />
      <path d="M4,4 L8,8 M8,4 L4,8" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
    </>
  );
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: "12px", padding: "18px" }}>
      <div style={{ fontSize: "11px", letterSpacing: "0.22em", color: labelColor, fontFamily: "Jost, sans-serif", marginBottom: "14px", fontWeight: 500 }}>
        {label}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {items.map((it, i) => (
          <li key={`${it}-${i}`} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "10px" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0, marginTop: "4px" }}>
              {iconPath}
            </svg>
            <span style={{ fontSize: "13px", lineHeight: 1.6, color: "#1A1030", fontFamily: "Georgia, serif" }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReportSection({ title, body }) {
  return (
    <div className="mt-5">
      <div className="ph-overline">{title}</div>
      <p className="text-[#2C0A5E] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function buildWhatsappMessage(order, ctx = {}) {
  const { curYr, nxtYr, pyCur, pyNxt, chakra } = ctx;
  const elName = ELEMENTS[order.dominantElement]?.name || order.dominantElement;
  const elSanskrit = ELEMENTS[order.dominantElement]?.sanskrit || "";
  const lines = [
    `Namaste ${order.fullName.split(" ")[0]},`,
    ``,
    `Your Pancha Healing prescription is ready.`,
    ``,
    `Life Path: ${order.numerology.lifePath} | Name Number: ${order.numerology.nameNumber} | Zodiac: ${order.numerology.zodiac}`,
    `Dominant Element: ${elName}${elSanskrit ? ` (${elSanskrit})` : ""}`,
  ];
  if (chakra) {
    lines.push(`Dominant Chakra: ${chakra.name} (${chakra.english}) — Mantra: ${chakra.mantra}`);
  }
  if (curYr && pyCur != null) lines.push(`Personal Year ${curYr}: ${pyCur}`);
  if (nxtYr && pyNxt != null) lines.push(`Next Year (${nxtYr}): ${pyNxt}`);
  lines.push(
    ``,
    `Pancha Foundation (5 x 8mm):`,
    ...order.panchaBeads.map((b) => ` - ${b.sanskrit} (${b.element}): ${b.crystal}`),
    ``,
    `Prescription Crystals (8mm):`,
    ...order.prescriptionCrystals.map((c) => ` - ${c.crystal} x${c.beadCount} - ${c.source} - ${c.reason}`),
    ``,
    `Bead Arrangement: Pancha beads are spaced equally around the bracelet with prescription beads between each pair.`,
    ``,
    `Order: ${PRODUCT.name} - Rs ${PRODUCT.price}`,
    `Order Number: ${order.orderNumber}`,
    ``,
    `Your full Sacred Report is on its way to your email (${order.email}).`,
    ``,
    `With gratitude, Pancha Healing.`,
  );
  return lines.join("\n");
}

function buildEmailContent(order, report, ctx = {}) {
  const first = order.fullName.split(" ")[0];
  const { curYr, nxtYr, pyCur, pyNxt, chakra } = ctx;
  const lines = [
    `Subject: Your Sacred Numerology Report - Pancha Healing`,
    ``,
    `Namaste ${first},`,
    ``,
    `With deep gratitude, here is your complete Sacred Numerology Report from Pancha Healing.`,
    ``,
    `YOUR SACRED BLUEPRINT`,
    `---------------------`,
    `Life Path: ${order.numerology.lifePath}`,
    `Name Number: ${order.numerology.nameNumber}`,
    `Zodiac: ${order.numerology.zodiac}`,
    curYr && pyCur != null ? `Personal Year ${curYr}: ${pyCur}` : null,
    nxtYr && pyNxt != null ? `Personal Year ${nxtYr}: ${pyNxt}` : null,
    `Dominant Element: ${order.dominantElement}`,
    chakra ? `Dominant Chakra: ${chakra.name} (${chakra.english}) - Mantra: ${chakra.mantra}` : null,
    `Lucky Color: ${order.numerology.luckyColor}`,
    `Lucky Day: ${order.numerology.luckyDay}`,
    `Lucky Numbers: ${Array.isArray(order.numerology.luckyNumbers) ? order.numerology.luckyNumbers.join(", ") : "\u2014"}`,
    `Ruling Planet: ${order.numerology.rulingPlanet}`,
    `Birth Month: ${order.numerology.birthMonth}`,
    ``,
    `SOUL ESSENCE`,
    `------------`,
    report.soul,
    ``,
    `LIFE PATH ${order.numerology.lifePath} - SOUL MISSION`,
    `-----------------`,
    report.lp,
    ``,
    `NAME NUMBER ${order.numerology.nameNumber} - NAME ENERGY`,
    `-------------------`,
    report.nn,
    ``,
    `CORE CHARACTERISTICS`,
    `--------------------`,
    report.ch,
    ``,
    curYr && pyCur != null ? `CURRENT LIFE PHASE - PERSONAL YEAR ${pyCur} (${curYr})` : `CURRENT LIFE PHASE`,
    `------------------`,
    report.cur,
    ``,
    nxtYr && pyNxt != null ? `ENERGY AHEAD - ${nxtYr} PERSONAL YEAR ${pyNxt}` : `ENERGY AHEAD`,
    `------------`,
    report.fut,
    ``,
    `KARMA LESSON THIS LIFETIME`,
    `------------`,
    report.kar,
    ``,
    `YOUR SACRED GIFTS`,
    `-----------------`,
    report.gifts,
    ``,
    `SPIRITUAL PRACTICES FOR YOU`,
    `---------------------------`,
    report.prac,
    ``,
    `SACRED GUIDANCE - DOS`,
    `---------------------`,
    ...report.dos.map((d) => `- ${d}`),
    ``,
    `SACRED GUIDANCE - DONTS`,
    `-----------------------`,
    ...report.dont.map((d) => `- ${d}`),
    ``,
    `PERSONAL AFFIRMATION`,
    `--------------------`,
    report.aff,
    ``,
    `PANCHA FOUNDATION CRYSTALS (8mm beads)`,
    `----------------------------------------`,
    ...order.panchaBeads.map((b) => `${b.sanskrit} (${b.element}): ${b.crystal}`),
    ``,
    `PRESCRIPTION CRYSTALS (8mm beads)`,
    `---------------------------------`,
    ...order.prescriptionCrystals.map((c) => `${c.crystal} x${c.beadCount} - ${c.source} - ${c.reason}`),
    ``,
    `Your bracelet is being handcrafted now and will be dispatched within 3 to 5 working days.`,
    ``,
    `With love and light,`,
    `Pancha Healing`,
    `WhatsApp: ${BRAND.whatsappDisplay}`,
    `Email: ${BRAND.email}`,
  ].filter((line) => line !== null);
  return lines.join("\n");
}



// ============= Delivery Address Editor =============
const INDIAN_STATES_ADMIN = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

function DeliveryAddressEditor({ order, onSaved }) {
  const addr = order.deliveryAddress || {};
  const [editing, setEditing] = useState(!addr.pincode);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: addr.fullName || order.fullName || "",
    mobile: (addr.mobile || (order.whatsapp || "").replace(/\D/g, "").slice(-10)) || "",
    line1: addr.line1 || "",
    line2: addr.line2 || "",
    landmark: addr.landmark || "",
    city: addr.city || "",
    state: addr.state || "",
    pincode: addr.pincode || "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.fullName.trim().length > 1 &&
    /^\d{10}$/.test(form.mobile) &&
    form.line1.trim().length > 2 &&
    form.city.trim().length > 1 &&
    form.state &&
    /^\d{6}$/.test(form.pincode);

  const save = async () => {
    if (!valid) {
      toast.error("Fix highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const updated = await ordersApi.saveAddress(order.id, {
        fullName: form.fullName.trim(),
        mobile: form.mobile,
        line1: form.line1.trim(),
        line2: form.line2.trim(),
        landmark: form.landmark.trim(),
        city: form.city.trim(),
        state: form.state,
        pincode: form.pincode,
      });
      onSaved(updated);
      setEditing(false);
      toast.success("Address saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  if (!editing && addr.pincode) {
    return (
      <div className="mt-2" data-testid="admin-address-view">
        <div className="text-sm text-[#2C0A5E] leading-relaxed">
          <div className="font-medium">{addr.fullName}</div>
          <div>{addr.line1}</div>
          {addr.line2 && <div>{addr.line2}</div>}
          {addr.landmark && <div className="text-[#6B5E7E]">Landmark: {addr.landmark}</div>}
          <div>{addr.city}, {addr.state} {addr.pincode}</div>
          <div className="mt-1 text-[#6B5E7E]">Mobile: +91 {addr.mobile}</div>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-3 text-xs tracking-[0.2em] uppercase text-[#6A0DAD] hover:underline"
          data-testid="admin-address-edit"
        >
          Edit address →
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 grid sm:grid-cols-2 gap-3" data-testid="admin-address-editor">
      <input className="ph-input" placeholder="Full name" value={form.fullName} onChange={(e) => setField("fullName", e.target.value)} data-testid="admin-addr-fullname" />
      <input className="ph-input" placeholder="Mobile (10 digits)" inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="admin-addr-mobile" />
      <input className="ph-input sm:col-span-2" placeholder="Address line 1 (house/flat, street)" value={form.line1} onChange={(e) => setField("line1", e.target.value)} data-testid="admin-addr-line1" />
      <input className="ph-input sm:col-span-2" placeholder="Address line 2 / Landmark (optional)" value={form.line2} onChange={(e) => setField("line2", e.target.value)} data-testid="admin-addr-line2" />
      <input className="ph-input" placeholder="City" value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="admin-addr-city" />
      <select className="ph-input" value={form.state} onChange={(e) => setField("state", e.target.value)} data-testid="admin-addr-state">
        <option value="">Select state…</option>
        {INDIAN_STATES_ADMIN.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input className="ph-input" placeholder="PIN (6 digits)" inputMode="numeric" maxLength={6} value={form.pincode} onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="admin-addr-pincode" />
      <div className="sm:col-span-2 flex gap-3 items-center">
        <button type="button" onClick={save} disabled={!valid || saving} className="ph-btn-primary" data-testid="admin-addr-save">
          {saving ? "Saving…" : "Save address"}
        </button>
        {addr.pincode && (
          <button type="button" onClick={() => setEditing(false)} className="ph-btn-ghost" data-testid="admin-addr-cancel">Cancel</button>
        )}
      </div>
    </div>
  );
}
